/** Bearer JWT for KubAIr API (local auth + protected routes). */

import axios from 'axios';
import { supabase, supabaseEnabled } from './supabase';

const TOKEN_KEY = 'yenom-access-token';
export const SESSION_EXPIRED_EVENT = 'yenom:session-expired';
let _axiosInterceptorAttached = false;

/** Single-flight Supabase token refresh: many parallel 401s share one refresh.
 *  Uses getSession() first — it waits for supabase-js to finish loading the
 *  persisted session and auto-refreshes an expired access token, which
 *  refreshSession() alone does not guarantee during app boot. */
let _refreshPromise = null;
async function refreshSupabaseToken() {
  if (!supabaseEnabled || !supabase) return null;
  if (!_refreshPromise) {
    _refreshPromise = (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.access_token) return data.session.access_token;
      } catch { /* fall through to explicit refresh */ }
      try {
        const { data } = await supabase.auth.refreshSession();
        return data?.session?.access_token || null;
      } catch {
        return null;
      }
    })().finally(() => {
      // Allow a fresh refresh attempt on the next 401 burst.
      setTimeout(() => { _refreshPromise = null; }, 250);
    });
  }
  return _refreshPromise;
}

/** Attach Authorization header to all axios requests (same-origin / backend URL),
 *  and recover from 401s by refreshing the Supabase session once and retrying.
 *  If refresh fails, emit SESSION_EXPIRED_EVENT so the auth store can log out
 *  cleanly instead of the UI silently rendering empty data. */
export function attachAxiosAuthInterceptor() {
  if (_axiosInterceptorAttached) return;
  _axiosInterceptorAttached = true;
  axios.interceptors.request.use((config) => {
    const h = authHeaders();
    if (h.Authorization) {
      config.headers = config.headers || {};
      Object.assign(config.headers, h);
    }
    return config;
  });

  axios.interceptors.response.use(
    (res) => res,
    async (error) => {
      const config = error?.config;
      const status = error?.response?.status;
      if (status === 401 && config && !config._retriedAfter401) {
        config._retriedAfter401 = true;
        const token = await refreshSupabaseToken();
        if (token) {
          setAccessToken(token);
          config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
          return axios(config);
        }
        try {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        } catch { /* SSR / test env — ignore */ }
      }
      return Promise.reject(error);
    },
  );
}

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setAccessToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
}

export function clearAccessToken() {
  setAccessToken(null);
}

/** Headers to merge into fetch / axios for API calls. */
export function authHeaders() {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
